/**
 * Adapted Previews Hook (Stufe 4.5: All-Position Adaptation)
 *
 * Computes per-product, per-position design adaptation placements for
 * ProductCard mockups. Uses the shared adaptation logic to determine where
 * a design should appear on each target product's PrintArea based on the
 * source product's placement.
 *
 * Stufe 4.5 Change: Computes adaptations for ALL positions (front, back, etc.),
 * not just the activePosition. This enables ProductGrid to show correct
 * adapted mockups on all views simultaneously.
 *
 * Performance:
 * - Memoized: only recalculates when design state or products change
 * - No per-product canvas rendering; CSS positioning handles display
 */

import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  areaFromPrintContext,
  carryOverPlacement,
  placementFromRelative,
  type AdaptedPlacement,
  type PlacementArea,
  type PrintContextLike,
  type RelativeDesignState,
} from '@designdeck/shared';

import {
  useCanvasStateManager,
  type PositionState,
  type ProductDesignCrop,
} from '@/stores/canvasStateManager';
import type { UIProduct } from '@/lib/product-transformer';

// =============================================================================
// TYPES
// =============================================================================

/** Adaptation data for a single position on a single product */
export interface PerPositionAdaptation {
  /** CSS-ready placement (all values 0-1 relative to target PrintArea) */
  placement: AdaptedPlacement;
  /** Effective crop image source (DataURL or S3 URL) */
  cropUrl: string | null;
  /** Whether this is a user-customized placement (not auto-adapted) */
  isCustomPlacement?: boolean;
}

/** The template fields the placement area is read from. */
export interface PlacementTemplateLike {
  /** The template's rectangle (`templateRect`, product-transformer). */
  printAreaRect: { width: number; height: number };
  printAreaWidthMm: number;
  printAreaHeightMm: number;
  printAreaSizeTier?: PlacementArea['sizeTier'];
  printAreaSizeTierOverride?: PlacementArea['sizeTier'] | null;
  productId?: string;
  printAreaProductClass?: PlacementArea['productClass'];
  printAreaCarryOverPercent?: number | null;
  printAreaCarryOverAnchor?: PlacementArea['carryOverAnchor'];
  printAreaCarryOverTopOffsetMm?: number | null;
}

/**
 * The placement area of a product's mockup template: pixel size from the
 * template rectangle (same as MockupRenderer), the area's millimetres,
 * effective size tier, product identity and the effective carry-over
 * settings (the transformer fills them from product and area). Null when
 * the rectangle is degenerate.
 */
export function placementAreaOfTemplate(
  template: PlacementTemplateLike
): PlacementArea | null {
  const { width, height } = template.printAreaRect;
  if (!(width > 0) || !(height > 0)) return null;
  const area: PlacementArea = {
    width,
    height,
    maxWidthMm: template.printAreaWidthMm,
    maxHeightMm: template.printAreaHeightMm,
    sizeTier: template.printAreaSizeTier,
    // A RUNDUM marker (or a KLEIN value the retire migration has not
    // removed yet) has to reach the carry-over. The transformer is the only
    // source; the raw print area is not consulted.
    sizeTierOverride: template.printAreaSizeTierOverride ?? null,
  };
  if (template.productId) area.productId = template.productId;
  if (template.printAreaProductClass !== undefined) {
    area.productClass = template.printAreaProductClass;
  }
  if (template.printAreaCarryOverPercent != null) {
    area.carryOverPercent = template.printAreaCarryOverPercent;
  }
  if (template.printAreaCarryOverAnchor != null) {
    area.carryOverAnchor = template.printAreaCarryOverAnchor;
  }
  if (template.printAreaCarryOverTopOffsetMm != null) {
    area.carryOverTopOffsetMm = template.printAreaCarryOverTopOffsetMm;
  }
  return area;
}

export interface AdaptedPreviewData {
  /**
   * Nested adaptation map: productId → positionSlug → PerPositionAdaptation
   * null when no positions have design data.
   *
   * Stufe 4.5: Now covers ALL positions, not just activePosition.
   */
  adaptations: Map<string, Map<string, PerPositionAdaptation>> | null;
}

// =============================================================================
// SELECTORS (stable references to avoid re-renders)
//
// IMPORTANT: Selectors must return STABLE references to avoid unnecessary
// re-renders. useShallow compares by shallow equality — creating new objects
// inside a selector defeats memoization.
// =============================================================================

/**
 * Stufe 4.5: Select masterPositions record directly (stable reference).
 * Filtering + transformation happens in useMemo, not in the selector,
 * to avoid creating new object references on every store notification.
 */
const selectMasterPositions = (
  state: ReturnType<typeof useCanvasStateManager.getState>
): Record<string, PositionState> => state.masterPositions;

/**
 * Stufe 5+: Select per-product placements (stable reference from store).
 */
const selectPlacementsByProduct = (
  state: ReturnType<typeof useCanvasStateManager.getState>
): Record<string, Record<string, RelativeDesignState>> =>
  state.placementsByProduct;

/**
 * Select per-product independent design crops (stable reference from store).
 */
const selectProductDesignCrops = (
  state: ReturnType<typeof useCanvasStateManager.getState>
): Record<string, Record<string, ProductDesignCrop>> =>
  state.productDesignCrops;

/** The design's master product, set by the design store on load and save. */
const selectMasterProductId = (
  state: ReturnType<typeof useCanvasStateManager.getState>
): string | null => state.masterProductId;

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to compute adapted design placements for all products across all positions.
 *
 * Stufe 4.5: Now computes adaptations for ALL positions that have a design,
 * not just the activePosition. This enables ProductGrid to show correct
 * adapted mockups on front AND back simultaneously.
 *
 * @param products - Array of UI products to compute adaptations for
 *   (MUST be memoized by caller to avoid unnecessary recomputes)
 *
 * Same rule on every step: a product with a custom placement (independent
 * design crop or persisted per-product placement) is shown with
 * `placementFromRelative`, unclamped, on its own print area; every other
 * product carries the master placement over via `carryOverPlacement`.
 * @returns AdaptedPreviewData with per-product, per-position placements
 */
export function useAdaptedPreviews(products: UIProduct[]): AdaptedPreviewData {
  // Select raw store references — useShallow ensures re-render only when
  // the top-level record reference changes (e.g. after position save/load).
  const masterPositions = useCanvasStateManager(
    useShallow(selectMasterPositions)
  );

  // Stufe 5+: Per-product placements (stable reference from store)
  const placementsByProduct = useCanvasStateManager(selectPlacementsByProduct);

  // Per-product independent design crops (products with their own canvas data)
  const productDesignCrops = useCanvasStateManager(selectProductDesignCrops);

  // The master decides rule 1 for live contexts without a product id.
  const masterProductId = useCanvasStateManager(selectMasterProductId);

  // Compute per-product, per-position adaptations (pure math, memoized).
  // Filtering (only positions with relativeState) and transformation
  // (printContext extraction, cropUrl resolution) happen here, not in selectors,
  // to keep selector references stable.
  const adaptations = useMemo(() => {
    const productMap = new Map<string, Map<string, PerPositionAdaptation>>();

    // Build position entries with design data (filter + transform)
    const positionEntries: Array<{
      slug: string;
      relativeState: RelativeDesignState;
      cropUrl: string | null;
      printContext: PrintContextLike;
    }> = [];

    for (const [slug, pos] of Object.entries(masterPositions)) {
      if (!pos?.relativeState || !pos?.printContext) continue;
      positionEntries.push({
        slug,
        relativeState: pos.relativeState,
        // Prefer fresh DataURL over persisted S3 URL
        cropUrl: pos.designCropDataUrl ?? pos.designCropUrl ?? null,
        printContext: pos.printContext,
      });
    }

    if (positionEntries.length === 0) return null;

    for (const product of products) {
      const positionMap = new Map<string, PerPositionAdaptation>();
      const productCrops = productDesignCrops[product.id];

      for (const posEntry of positionEntries) {
        // If this product has its own independent design for this position,
        // use it directly instead of adapting from the master design.
        const independentCrop = productCrops?.[posEntry.slug];
        if (independentCrop?.relativeState && independentCrop?.cropUrl) {
          const template = product.mockupTemplates.find(
            (t) => t.positionKey === posEntry.slug
          );
          const target = template ? placementAreaOfTemplate(template) : null;
          if (target) {
            positionMap.set(posEntry.slug, {
              placement: placementFromRelative(
                independentCrop.relativeState,
                target
              ),
              cropUrl: independentCrop.cropUrl,
            });
            continue;
          }
        }

        // Find the matching mockup template for this position on this product
        const template = product.mockupTemplates.find(
          (t) => t.positionKey === posEntry.slug
        );
        if (!template) continue;

        // Use printArea native dimensions (same as MockupRenderer) for accurate
        // design positioning, falling back to printAreaWidthPx/printAreaHeightPx.
        const target = placementAreaOfTemplate(template);
        if (!target) continue;

        const persistedPlacement =
          placementsByProduct[product.id]?.[posEntry.slug];

        // Determine the effective crop URL: prefer the product's own saved crop
        // (batch-applied or independently edited) over the master crop.
        const productCropEntry = productCrops?.[posEntry.slug];
        const productCropUrl = productCropEntry?.cropUrl ?? null;
        const effectiveCropUrl = productCropUrl || posEntry.cropUrl;

        // One rule for cards, canvas and persistence: a per-product placement
        // is shown as-is on its own print area; everything else carries the
        // live state over (percents, classes, tiers and overflow included).
        // The product the state was measured on shows it unchanged: by id
        // when the context has one, by the master for contexts without.
        // The card clips to the print area — see
        // docs/features/FEATURE_PRINT_AREA_ADAPTATION.md.
        const finalPlacement = persistedPlacement
          ? placementFromRelative(persistedPlacement, target)
          : placementFromRelative(
              carryOverPlacement(
                posEntry.relativeState,
                areaFromPrintContext(posEntry.printContext),
                target,
                { isMaster: product.id === masterProductId }
              ),
              target
            );

        positionMap.set(posEntry.slug, {
          placement: finalPlacement,
          cropUrl: effectiveCropUrl,
        });
      }

      if (positionMap.size > 0) {
        productMap.set(product.id, positionMap);
      }
    }

    return productMap.size > 0 ? productMap : null;
  }, [
    masterPositions,
    products,
    placementsByProduct,
    productDesignCrops,
    masterProductId,
  ]);

  return { adaptations };
}
